import { BitkitTour, useBitkitTour } from '@bitrise/bitkit-v2';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  holdOnboardingChecklist,
  releaseOnboardingChecklist,
} from '@/components/OnboardingChecklist/onboardingChecklistGate';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { setSettingsNavHrefOverride } from '@/components/page/PlatformShell/UISideNav/settingsNavOverride';
import { WorkspaceSettingsPage } from '@/enums/WorkspaceSettingsPage';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { GlobalProps } from '@/models/GlobalProps';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { workspaceSettingsPath } from '@/utils/monolithApiRouteService';
import { getCsrfTokenCookie } from '@/utils/tokens';

const AI_CONFIG_SIGNPOST_NOTIFICATION = 'ai-config-signpost';

export const AI_FEATURES_DOCS_URL = 'https://docs.bitrise.io/en/bitrise-platform/ai/ai-features-on-bitrise.html';

// Avo branch c0mRuS_IRttGjs006vt5k (CI-6462). `source` and every dismiss method below are enum
// values of the plan — the plan is the source of truth for all of them, not this file.
const SIGNPOST_SOURCE = 'build_details_page_ai_config_signpost';
const SHOWN_EVENT = 'Ai Config Signpost Shown';
const DISMISSED_EVENT = 'Ai Config Signpost Dismissed';
const SETTINGS_OPENED_EVENT = 'Bitrise Ai Settings Opened';
const DOCS_OPENED_EVENT = 'Documentation Article Opened';

type DismissMethod = 'acknowledge_button' | 'close_button' | 'other';

// Long enough for the build page to settle before something pops up over it.
const OPEN_DELAY_MS = 1000;
// The checklist owns the same corner, so it waits out the signpost and then this beat on top.
const CHECKLIST_RELEASE_DELAY_MS = 1000;

const SETTINGS_NAV_SELECTOR = '.platform-nav-settings';

// The gear in the workspace settings nav button. The icon rather than the button itself: the rail is
// 72px wide with overflow hidden while every button in it is 264px, so the button's rect is mostly
// off-rail and the spotlight would miss what the user sees. A DOM lookup rather than a ref because
// the nav lives in the shell around this page.
const settingsNavIcon = () => document.querySelector(`${SETTINGS_NAV_SELECTOR} svg`) as HTMLElement | null;

/**
 * The one-time highlight pointing at the workspace settings nav, shown to whoever can turn the
 * workspace AI setting off when they open a build of a project whose CI config the AI generator
 * wrote. Eligibility — including whether they have already dismissed it in this workspace — is
 * settled by the backend, so this only owns the dismissal.
 */
const AIConfigSignpost = () => {
  const { account } = useContext(GlobalPropsContext) as GlobalProps;
  const { app, build, aiConfigSignpost } = useBuildDetailsPageStore();

  const [isOpen, setIsOpen] = useState(false);
  // Both dismiss triggers can fire for one gesture — the ✕ and "Got it" — and the tour reports the
  // close back to us either way. Refs rather than state, so a second call in the same tick is caught
  // too, and so the listeners below read the current value without re-subscribing.
  const hasDismissed = useRef(false);
  const dismissMethod = useRef<DismissMethod | null>(null);
  const hasOpenedSettings = useRef(false);
  const releaseTimer = useRef<number>(undefined);

  const eventProps = useCallback(
    () => ({ app_slug: app.slug, build_slug: build.slug, platform: 'website' as const }),
    [app.slug, build.slug],
  );

  const recordDismissal = useCallback(() => {
    // One of the dismiss triggers is the gear itself, which is a link — the browser aborts in-flight
    // XHRs as it navigates away, so an axios mutation here would frequently never reach the server
    // and the signpost would come back. `keepalive` lets the request outlive the teardown.
    fetch('/notifications/dismiss', {
      method: 'POST',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCsrfTokenCookie(),
      },
      body: JSON.stringify({ name: AI_CONFIG_SIGNPOST_NOTIFICATION }),
    }).catch(() => {
      // Losing the dismissal only means the signpost shows again next visit; never break the page.
    });
  }, []);

  useEffect(() => {
    if (!aiConfigSignpost) {
      return undefined;
    }

    holdOnboardingChecklist(AI_CONFIG_SIGNPOST_NOTIFICATION);
    const openTimer = window.setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);

    return () => {
      window.clearTimeout(openTimer);
      // A pending release would otherwise land after this mount is gone and open the gate under
      // whoever holds it next — the holder name is the same on every mount, so it would match.
      window.clearTimeout(releaseTimer.current);
      releaseOnboardingChecklist(AI_CONFIG_SIGNPOST_NOTIFICATION);
    };
  }, [aiConfigSignpost]);

  useEffect(() => {
    if (!isOpen || !account) {
      return undefined;
    }

    setSettingsNavHrefOverride(workspaceSettingsPath(account.slug, WorkspaceSettingsPage.AI));

    // Esc is the only dismissal the tour reports without one of its own controls being clicked, so
    // it is recognised here rather than inferred from the absence of the others.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissMethod.current = 'other';
      }
    };

    // Both links live outside this component's own markup — the gear in the shell's nav, "Learn
    // more" inside the tour's portal — so one delegated listener covers them.
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;

      if (target?.closest(SETTINGS_NAV_SELECTOR)) {
        hasOpenedSettings.current = true;
        segmentTrack(SETTINGS_OPENED_EVENT, { ...eventProps(), source: SIGNPOST_SOURCE });
        // Acting on the signpost settles it: the page is about to navigate away, so this is the last
        // chance to keep it from returning on the next build.
        if (!hasDismissed.current) {
          hasDismissed.current = true;
          recordDismissal();
        }
        return;
      }

      if (target?.closest(`a[href="${AI_FEATURES_DOCS_URL}"]`)) {
        segmentTrack(DOCS_OPENED_EVENT, {
          ...eventProps(),
          source: SIGNPOST_SOURCE,
          link_url: AI_FEATURES_DOCS_URL,
        });
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('click', onClick, true);

    return () => {
      setSettingsNavHrefOverride(null);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [account, eventProps, isOpen, recordDismissal]);

  const tour = useBitkitTour({
    onStatusChange: ({ status }) => {
      if (status === 'started') {
        segmentTrack(SHOWN_EVENT, eventProps());
        return;
      }

      if (status !== 'dismissed') {
        return;
      }

      setIsOpen(false);
      releaseTimer.current = window.setTimeout(
        () => releaseOnboardingChecklist(AI_CONFIG_SIGNPOST_NOTIFICATION),
        CHECKLIST_RELEASE_DELAY_MS,
      );

      // The gear closes the popover for good as well, but it reports itself as Bitrise Ai Settings
      // Opened — the plan keeps the two entry points apart.
      if (hasOpenedSettings.current || hasDismissed.current) {
        return;
      }

      hasDismissed.current = true;
      segmentTrack(DISMISSED_EVENT, {
        ...eventProps(),
        popup_dismiss_method: dismissMethod.current ?? 'close_button',
      });
      recordDismissal();
    },
    open: isOpen,
    steps: [
      {
        actions: [
          {
            action: ({ dismiss }) => {
              dismissMethod.current = 'acknowledge_button';
              dismiss();
            },
            label: 'Got it',
          },
        ],
        description:
          'We use AI to generate CI configs for new projects. You can turn this off anytime in workspace settings.',
        id: 'ai-config-signpost',
        secondaryAction: { href: AI_FEATURES_DOCS_URL, isExternal: true, label: 'Learn more' },
        target: settingsNavIcon,
        title: 'AI features are enabled',
      },
    ],
  });

  return <BitkitTour tour={tour} />;
};

export default AIConfigSignpost;
